import {
  ActionMenu,
  DotMenuHorizontalIcon,
  TabItem,
  Tabs,
  getAnonymityModeKeyForShowingAnonymityInfo,
  getFrontendConfigValue,
  theme,
  useToast,
  usePath,
} from '@arnold/common';
import { REPORT_LINK } from '@arnold/core';
import styled from '@emotion/styled/macro';
import { subDays } from 'date-fns';
import { FC, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import { useHistory } from 'react-router-dom';
import { GraphQLErrorCard } from '../../components';
import { PageLayout } from '../../components/Common/PageLayout';
import Loading from '../../components/Loading';
import { ReportPendingApprovalTooltip } from '../../components/ReportPendingApprovalTooltip';
import { SurveyDetailContent } from '../../components/StyledComponents';
import ArchiveSurvey from '../../components/SurveysOverview/ArchiveSurvey';
import useArchive from '../../components/SurveysOverview/useArchive';
import {
  AnonymityLevel,
  IsAccessTokenValidQuery,
  SurveyStatus,
  useActiveRespondentsQuery,
  useSurveyQuery,
  useUploadSurveyAttachmentMutation,
  useUserQuery,
} from '../../generated/hooks';
import ee from '../../lib/eventEmitter';
import { getTopLevelReportAccess, goToTalkResults, isSurveyFinished, isSurveyPlanned } from '../../lib/helpers';
import i18n from '../../translations/i18n';
import { AddRespondentToOngoingSurveyScreen } from './AddRespondentToOngoingSurveyScreen';
import EditSurveyParticipants from './EditSurveyParticipants';
import SurveyAttachments from './SurveyAttachments';
import { SurveyDetailTable } from './SurveyDetailTable';
import { SurveyInfo } from './SurveyInfo';
import SurveyReportAccess from './SurveyReportAccess';
import SurveyScheduling from './SurveyScheduling';

type Props = RouteComponentProps<any> & {
  user: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']['user']>;
};

const MAX_ATTACHMENT_SIZE_BYTES = 50 * 1024 * 1024;

const AddRespondentsButton = styled(Button)`
  margin-left: 12px;
  white-space: nowrap;
`;

const ArchivedSurveyText = styled.span`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.body.small.regular.fontSize};
  font-weight: ${theme.typography.body.small.regular.fontWeight};
  line-height: ${theme.typography.body.small.regular.lineHeight};
`;

export const PATHS = {
  RESPONDENTS: 'respondents',
  EDIT_RESPONDENTS: 'editRespondents',
  RESULTS: 'results',
  SCHEDULING: 'scheduling',
  ADD_RESPONDENTS_TO_ONGOING_SURVEY: 'addRespondents',
};

export const SurveyDetailScreen: FC<Props> = ({
  match: {
    params: { surveyId, type },
  },
  user,
}) => {
  const [t] = useTranslation('survey');
  const [surveysOverviewT] = useTranslation('surveysOverview');
  const { loading, data } = useSurveyQuery({ variables: { surveyId }, fetchPolicy: 'network-only' });

  const history = useHistory();
  const { surveyArchivePath, surveysPath, surveyDetailPath, reopenSurveyPath } = usePath();

  const { loading: respondentsLoading, data: respondentsData } = useActiveRespondentsQuery({ fetchPolicy: 'no-cache' });

  const { loading: userLoading, data: userData } = useUserQuery({
    variables: { languageCode: i18n.language },
    fetchPolicy: 'no-cache',
  });

  const { handleArchive, loading: archiveLoading } = useArchive({
    survey: data?.survey,
    organization: userData?.user.organization,
  });

  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadAttachment] = useUploadSurveyAttachmentMutation();

  const handleFilesSelected = async (fileList: FileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) {
      return;
    }
    setUploading(true);
    let uploadedCount = 0;
    let hadError = false;
    for (const file of files) {
      // Client-side guard mirrors the 50 MB server limit — locale-independent and avoids a wasted upload.
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        hadError = true;
        addToast(t('attachmentTooLarge'));
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadAttachment({
          variables: { surveyId, file },
          refetchQueries: ['surveyAttachments'],
          awaitRefetchQueries: true,
          context: { headers: { 'X-Apollo-Operation-Name': 'uploadSurveyAttachment' } },
        });
        uploadedCount += 1;
      } catch {
        hadError = true;
        addToast(t('attachmentUploadError'));
      }
    }
    if (uploadedCount > 0 && !hadError) {
      addToast(t('attachmentUploadSuccess'));
    }
    setUploading(false);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleFilesSelected(event.target.files);
    }
    event.target.value = '';
  };

  const lang = i18n.language;
  const [tabType, setTabType] = useState(Object.values(PATHS).includes(type) ? type : PATHS.RESULTS);

  useEffect(() => {
    setTabType(Object.values(PATHS).includes(type) ? type : PATHS.RESULTS);
  }, [type]);

  if (loading || respondentsLoading || userLoading || archiveLoading || !data || !userData) {
    return <Loading />;
  }

  if (!userData.user.organization?.surveys.find((survey) => survey.id === surveyId)) {
    return <GraphQLErrorCard />;
  }

  const respondents = data.survey?.whitelistedRespondents || [];
  const isArchived = data!.survey?.status === SurveyStatus.Archived;

  const headingTranslation =
    data.survey?.topicGroup?.translations?.find((translation) => translation.language.code === lang) ||
    data.survey?.topicGroup?.translations?.find(
      (translation) => translation.language.code === userData.user.organization?.primaryLanguageCode,
    );

  const emptyReport = getTopLevelReportAccess(data.survey?.reports);
  const reopenSurveyButton =
    data.survey?.status === SurveyStatus.Completed &&
    data.survey.endsAt &&
    new Date(data.survey.endsAt) >=
      subDays(new Date(), Number(getFrontendConfigValue('ALLOW_REOPENING_IN_DAYS', '14')));

  const headingButtons = () => {
    const isPlanned = isSurveyPlanned(data.survey!.status);
    const buttons = [];
    const menuItems = [];

    menuItems.push({
      label: t('attachmentUpload'),
      action: () => fileInputRef.current?.click(),
    });

    if (data.survey?.topic?.id || data.survey?.topicGroup?.id) {
      const targetId = data.survey.topicGroup?.id;
      menuItems.push({
        label: t('topicDetail'),
        action: () => {
          if (targetId) {
            return goToTopicDetail(targetId);
          }
        },
      });
    }

    if (data.survey?.status === SurveyStatus.Ongoing && !data.survey.public) {
      menuItems.push({
        label: t('addParticipants'),
        action: () => history.push(`/surveyDetail/${surveyId}/${PATHS.ADD_RESPONDENTS_TO_ONGOING_SURVEY}`),
      });
    }

    if (reopenSurveyButton) {
      menuItems.push({
        label: t('surveyOverviewRow:archive'),
        action: () => handleArchive(),
      });

      menuItems.push({
        label: t('reopenScreen:reopen'),
        action: () => reopenSurveyPath.toFunc(surveyId),
      });
    } else if (isSurveyFinished(data.survey!.status)) {
      buttons.push(
        <ArchiveSurvey
          survey={data.survey}
          organization={userData.user.organization}
          user={userData.user}
          showButton={true}
        />,
      );
    }

    if (!!menuItems.length) {
      buttons.push(
        <div className="d-inline-block my-5">
          <ActionMenu
            toggleIcon={<DotMenuHorizontalIcon />}
            items={menuItems}
            margin={`0 ${theme.spacing.f} 0 0`}
            dataIcom={'btn-dropdown'}
          />
        </div>,
      );
    }

    if (isPlanned) {
      buttons.push(
        <AddRespondentsButton
          onClick={() => history.push(`/surveyDetail/${surveyId}/${PATHS.EDIT_RESPONDENTS}`)}
          data-cy="change-respondents"
        >
          {t('changeParticipants')}
        </AddRespondentsButton>,
      );
    }

    if (data.survey?.public && data.survey?.status === SurveyStatus.Ongoing) {
      buttons.push(
        <Button
          onClick={() => ee.emitModalSharePublicSurvey(data.survey?.accessKey!, headingTranslation?.value || '')}
          variant={'primary'}
          size="sm"
        >
          {t('surveyForm:shareSurvey')}
        </Button>,
      );
    }

    if (!isPlanned) {
      const isReportPendingApproval = !!data.survey?.isReportPendingApproval;
      buttons.push(
        <ReportPendingApprovalTooltip isPending={isReportPendingApproval}>
          <Button
            onClick={() => {
              if (emptyReport?.accessKey && !isReportPendingApproval) {
                goToTalkResults(REPORT_LINK.REPORT, emptyReport.accessKey);
              }
            }}
            variant={'primary'}
            size="sm"
            disabled={!emptyReport?.accessKey || isReportPendingApproval}
          >
            {t('surveyOverviewRow:summaryReport')}
          </Button>
        </ReportPendingApprovalTooltip>,
      );
    }

    return buttons;
  };

  const answered = data.survey?.resultsInfo?.answered || 0;
  const total = data.survey?.resultsInfo?.total;

  const surveyInFuture =
    data.survey?.status === SurveyStatus.Prepared || data.survey?.status === SurveyStatus.Notstarted;
  const activeRespondents = respondentsData?.user.organization?.activeRespondents || [];

  const surveyStatusForBreadcrumbs =
    data.survey?.status === SurveyStatus.Activating ? SurveyStatus.Ongoing : data.survey?.status;
  const breadcrumbItems = isArchived
    ? [{ title: surveysOverviewT('surveyArchive'), link: surveyArchivePath.createPath() }]
    : [
        { title: surveysOverviewT('standaloneSurveys'), link: surveysPath.createPath() },
        {
          title: t(`status-${surveyStatusForBreadcrumbs}`),
          link: surveysPath.createPath(surveyStatusForBreadcrumbs?.toLowerCase() || '/'),
        },
      ];

  if (tabType === PATHS.EDIT_RESPONDENTS) {
    return (
      <EditSurveyParticipants
        respondents={respondents}
        notStarted={surveyInFuture}
        activeRespondents={activeRespondents}
        teams={respondentsData?.user.organization?.teams || []}
        surveyId={Number.parseInt(surveyId, 10)}
        user={userData.user}
        sendEmailSms={!!data.survey?.sendEmailSms}
        organization={userData.user.organization}
        closeForm={() => surveyDetailPath.toFunc(surveyId, PATHS.RESPONDENTS)}
        breadcrumbItems={[
          ...breadcrumbItems,
          { title: headingTranslation?.value ?? '', link: surveyDetailPath.createPath(surveyId) },
        ]}
      />
    );
  }

  if (tabType === PATHS.ADD_RESPONDENTS_TO_ONGOING_SURVEY) {
    return (
      <AddRespondentToOngoingSurveyScreen
        respondentIds={data.survey?.channels
          ?.filter((channel) => !!channel.respondent)
          .map((channel) => channel.respondent!.id)}
        breadcrumbItems={[
          ...breadcrumbItems,
          { title: headingTranslation?.value ?? '', link: surveyDetailPath.createPath(surveyId) },
        ]}
        id={data.survey?.id}
      />
    );
  }

  return (
    <PageLayout
      breadcrumbItems={breadcrumbItems}
      dataIcom={'heading-survey-title'}
      title={headingTranslation?.value}
      description={headingTranslation?.description || undefined}
      headingBox={
        <SurveyInfo
          answered={answered}
          total={total}
          status={data.survey?.status ?? ''}
          sendAt={new Date(data.survey?.sendAt).toLocaleDateString(lang)}
          endsAt={data.survey?.endsAt ? new Date(data.survey.endsAt).toLocaleDateString(lang) : undefined}
          isPublic={!!data.survey?.public}
          anonymityMode={
            data.survey?.anonymitySettings
              ? getAnonymityModeKeyForShowingAnonymityInfo(
                  data.survey.anonymitySettings.default ?? null,
                  data.survey.anonymitySettings.allowedLevels ?? [],
                )
              : 'nonAnonymousWithAnonymousOption'
          }
        />
      }
      buttons={headingButtons()}
    >
      <SurveyAttachments surveyId={String(surveyId)} uploading={uploading} />
      {!isArchived && (
        <Tabs
          items={
            [
              {
                title: t('reportSettings'),
                link: `/surveyDetail/${surveyId}/${PATHS.RESULTS}`,
                active: tabType === PATHS.RESULTS,
                icomTag: 'tab-survey-detail-report',
              },
              !data.survey?.public && {
                title: t('surveyTeams'),
                link: `/surveyDetail/${surveyId}/${PATHS.RESPONDENTS}`,
                active: tabType === PATHS.RESPONDENTS,
                icomTag: 'tab-survey-detail-teams',
              },
              {
                title: t('scheduling'),
                link: `/surveyDetail/${surveyId}/${PATHS.SCHEDULING}`,
                active: tabType === PATHS.SCHEDULING,
                icomTag: 'tab-survey-detail-scheduling',
              },
            ].filter(Boolean) as TabItem[]
          }
          theme={theme}
        />
      )}
      {isArchived ? (
        <SurveyDetailContent>
          <ArchivedSurveyText>{t('surveyIsArchived')}</ArchivedSurveyText>
        </SurveyDetailContent>
      ) : (
        <SurveyDetailContent>
          {tabType === PATHS.RESPONDENTS && (
            <SurveyDetailTable
              surveyTeamResults={data.survey?.teamsResultInfo?.filter(Boolean) || undefined}
              emptyReportAccess={emptyReport?.accessKey}
              isReportPendingApproval={!!data.survey?.isReportPendingApproval}
              channels={data.survey?.channels || []}
              isSurveyOngoing={data.survey?.status === SurveyStatus.Ongoing}
              surveyId={String(surveyId)}
              surveyEndsAt={data.survey?.endsAt || ''}
              isAnonymityEnabled={!!data.survey?.anonymitySettings.allowedLevels?.includes(AnonymityLevel.Organization)}
              organizationId={userData.user.organization?.id || ''}
            />
          )}

          {tabType === PATHS.RESULTS && <SurveyReportAccess surveyId={Number.parseInt(surveyId, 10)} user={user} />}

          {tabType === PATHS.SCHEDULING && (
            <SurveyScheduling
              surveyId={Number.parseInt(surveyId, 10)}
              notStarted={surveyInFuture}
              survey={data.survey}
              displayReopenButton={reopenSurveyButton}
              organization={userData.user.organization}
              user={userData.user}
            />
          )}
        </SurveyDetailContent>
      )}
      <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileInputChange} />
    </PageLayout>
  );
};

const goToTopicDetail = (topicId: string) => window.open(`/topicEditor/${topicId}`, '_self');
